import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Home,
  LocationOn,
  Person,
  Phone,
  Email,
  Star,
  CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export const TestRTLPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Language Switcher Demo */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1">
          {t('property_management')}
        </Typography>
        <LanguageSwitcher />
      </Box>

      {/* RTL Test Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {t('property_details')}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" paragraph>
                  {t('welcome_message')}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  Direction: {isRTL ? 'Right-to-Left (RTL)' : 'Left-to-Right (LTR)'}
                </Typography>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Home />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('property_name')} 
                    secondary="Sunset Villa Resort" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('address')} 
                    secondary="123 Main Street, City, Country" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('owner')} 
                    secondary="Ahmed Al-Rashid" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('phone')} 
                    secondary="+966 50 123 4567" 
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip icon={<Star />} label={t('facilities')} color="primary" />
                <Chip icon={<CheckCircle />} label={t('confirmed')} color="success" />
                <Chip label={t('type')} variant="outlined" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('actions')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button variant="contained" fullWidth startIcon={<Home />}>
                {t('view')} {t('properties')}
              </Button>
              <Button variant="outlined" fullWidth>
                {t('add')} {t('property_name')}
              </Button>
              <Button variant="text" fullWidth>
                {t('edit')} {t('property_details')}
              </Button>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('units')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('total')}: 12 {t('units')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('status')}: 8 {t('vacant')}, 4 {t('occupied')}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Translation Demo */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('language')} & RTL Demo
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">English Translations:</Typography>
              <Typography variant="body2">• {t('dashboard')}</Typography>
              <Typography variant="body2">• {t('properties')}</Typography>
              <Typography variant="body2">• {t('units')}</Typography>
              <Typography variant="body2">• {t('bookings')}</Typography>
              <Typography variant="body2">• {t('users')}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Form Elements:</Typography>
              <Typography variant="body2">• {t('save')}</Typography>
              <Typography variant="body2">• {t('cancel')}</Typography>
              <Typography variant="body2">• {t('edit')}</Typography>
              <Typography variant="body2">• {t('delete')}</Typography>
              <Typography variant="body2">• {t('add')}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TestRTLPage;
